import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type ListAccountsResult,
  type ListPeopleResult,
  type ListTerritoriesResult,
  type PreviewRebalanceResult,
} from '@whinerp/client';
import type { RuleNode, RuleSchema } from '@whinerp/contracts/rules';
import { Button, CapabilityUnavailable, ErrorState, Panel, RuleBuilder, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';
import { ruleBuilderLabels } from '../catalog/rule-labels.js';

/**
 * Setup > Territories (F-054, G-146).
 *
 * The UI pointer: "a flat list with a criteria builder and a live count of
 * matching records per territory." The count comes from the database, from
 * the same compiled rule that assigns companies. The overlap is on the row,
 * because "two territories matching the same account is the defect that
 * produces duplicate outreach" and a warning inside an editor nobody opens
 * warns nobody.
 *
 * Saving never moves anybody. Moving is the rebalance, previewed first.
 */

type Territory = ListTerritoriesResult['territories'][number];
const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';
const EMPTY: RuleNode = { all: [] } as RuleNode;

export function TerritoriesPanel(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-054');
  const [editing, setEditing] = useState<Territory | 'new' | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const territories = useQuery<ListTerritoriesResult, ApiError>({
    queryKey: ['territories'],
    queryFn: () => api.listTerritories({}),
    enabled: enabled.enabled,
  });
  const preview = useQuery<PreviewRebalanceResult, ApiError>({
    queryKey: ['territories', 'rebalance'],
    queryFn: () => api.previewRebalance({}),
    enabled: enabled.enabled && previewing,
  });
  const apply = useMutation<{ moved: number }, ApiError>({
    mutationFn: () => api.applyRebalance({ body: {} }),
    onSuccess: async () => {
      setPreviewing(false);
      await queryClient.invalidateQueries({ queryKey: ['territories'] });
    },
  });

  const heading = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <SectionLabel>{t('territory.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('territory.body')}</p>
      </div>
      {enabled.enabled && editing === null ? (
        <Button intent="secondary" size="small" data-territory-new onClick={() => setEditing('new')}>
          {t('territory.new')}
        </Button>
      ) : null}
    </div>
  );

  if (enabled.state === 'off' || enabled.state === 'plan-locked') {
    return (
      <Panel data-region="territories" id="territories">
        <div className="flex flex-col gap-3">
          {heading}
          <CapabilityUnavailable
            kind={enabled.state === 'off' ? 'module-off' : 'plan-locked'}
            capability={t('territory.title')}
            title={t('capability.moduleOff.title')}
            body={t('capability.moduleOff.body', { values: { capability: t('territory.title') } })}
            action={{ label: t('capability.moduleOff.action'), href: '/setup/modules' }}
          />
        </div>
      </Panel>
    );
  }
  if (territories.isPending) return <Skeleton className="h-block-md w-full" />;
  if (territories.isError) {
    return <ErrorState title={t('state.error')} body={errorText(territories.error)} onRetry={() => void territories.refetch()} />;
  }

  const data = territories.data;
  return (
    <Panel data-region="territories" id="territories">
      <div className="flex flex-col gap-4">
        {heading}

        {editing !== null ? (
          <TerritoryEditor
            territory={editing === 'new' ? null : editing}
            schema={data.schema}
            named={data.named.filter((entry) => editing !== 'new' && entry.territoryId === editing.territoryId)}
            onDone={() => setEditing(null)}
          />
        ) : data.territories.length === 0 ? (
          <p data-territories-none className="text-meta text-ink-meta">{t('territory.none')}</p>
        ) : (
          <ul className="flex flex-col">
            {data.territories.map((territory) => (
              <li key={territory.territoryId} data-territory={territory.territoryId} className="flex flex-col gap-1 border-t border-t-rule py-2 first:border-t-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-body text-ink">
                    {territory.name}
                    {territory.active ? null : <span className="ms-2 text-meta text-ink-meta">{t('territory.inactive')}</span>}
                  </span>
                  <span data-territory-counts className="text-meta text-ink-meta">
                    {t('territory.counts', {
                      values: {
                        matching: String(territory.matching),
                        assigned: String(territory.assigned),
                        priority: String(territory.priority),
                        who: territory.ownerName ?? territory.teamName ?? '',
                      },
                    })}
                  </span>
                  <Button intent="ghost" size="small" data-territory-edit={territory.territoryId} onClick={() => setEditing(territory)}>
                    {t('territory.edit')}
                  </Button>
                </div>
                {territory.overlapping > 0 ? (
                  <p data-territory-overlap className="rounded-control bg-warning-tint px-3 py-1 text-dense text-warning-text">
                    {t('territory.overlap', { values: { count: String(territory.overlapping) } })}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {editing === null && data.territories.length > 0 ? (
          <section className="flex flex-col gap-2">
            <h3 className="text-dense font-medium text-ink">{t('territory.rebalance')}</h3>
            <p className="text-meta text-ink-meta">{t('territory.rebalanceBody')}</p>
            {apply.data ? (
              <p data-rebalance-done className="text-meta text-success-text">
                {t('territory.rebalanced', { values: { count: String(apply.data.moved) } })}
              </p>
            ) : null}
            {apply.error ? <p className="text-dense text-danger-text">{errorText(apply.error)}</p> : null}
            {!previewing ? (
              <div>
                <Button intent="secondary" size="small" data-rebalance-preview onClick={() => setPreviewing(true)}>
                  {t('territory.previewRebalance')}
                </Button>
              </div>
            ) : preview.isPending ? (
              <Skeleton className="h-block-sm w-full" />
            ) : preview.isError ? (
              <p className="text-dense text-danger-text">{errorText(preview.error)}</p>
            ) : preview.data.total === 0 ? (
              <p data-rebalance-nothing className="text-meta text-ink-meta">{t('territory.nothingToMove')}</p>
            ) : (
              <div className="flex flex-col gap-2">
                <p data-rebalance-total className="text-dense text-ink">
                  {t('territory.wouldMove', { values: { total: String(preview.data.total), shown: String(preview.data.shown) } })}
                </p>
                <ul className="flex flex-col">
                  {preview.data.moves.map((move) => (
                    <li key={move.accountId} data-rebalance-move={move.accountId} className="text-meta text-ink-600">
                      {t('territory.move', {
                        values: {
                          company: move.company,
                          from: move.fromName ?? t('territory.noTerritory'),
                          to: move.toName ?? t('territory.noTerritory'),
                        },
                      })}
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2">
                  <Button intent="primary" size="small" data-rebalance-apply disabled={apply.isPending} onClick={() => apply.mutate()}>
                    {t('territory.applyRebalance', { values: { total: String(preview.data.total) } })}
                  </Button>
                  <Button intent="ghost" size="small" onClick={() => setPreviewing(false)}>
                    {t('territory.cancel')}
                  </Button>
                </div>
              </div>
            )}
          </section>
        ) : null}
      </div>
    </Panel>
  );
}

function TerritoryEditor({
  territory,
  schema,
  named,
  onDone,
}: {
  territory: Territory | null;
  schema: RuleSchema;
  named: Array<{ accountId: string; name: string }>;
  onDone: () => void;
}): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const [name, setName] = useState(territory?.name ?? '');
  const [criteria, setCriteria] = useState<RuleNode>((territory?.criteria as RuleNode | undefined) ?? EMPTY);
  const [ownerId, setOwnerId] = useState(territory?.ownerId ?? '');
  const [teamId, setTeamId] = useState(territory?.teamId ?? '');
  const [priority, setPriority] = useState(String(territory?.priority ?? 100));
  const [active, setActive] = useState(territory?.active ?? true);
  const [accounts, setAccounts] = useState(named);
  const [search, setSearch] = useState('');

  const people = useQuery<ListPeopleResult, ApiError>({ queryKey: ['people'], queryFn: () => api.listPeople({}) });
  const found = useQuery<ListAccountsResult, ApiError>({
    queryKey: ['accounts', 'territory-search', search],
    queryFn: () => api.listAccounts({ query: { search, limit: 10 } }),
    enabled: search.trim().length >= 2,
  });
  const save = useMutation<unknown, ApiError>({
    mutationFn: () =>
      api.saveTerritory({
        body: {
          territoryId: territory?.territoryId ?? null,
          name,
          criteria: criteria as never,
          ownerId: ownerId || null,
          teamId: teamId || null,
          priority: Number(priority),
          active,
          namedAccountIds: accounts.map((account) => account.accountId),
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['territories'] });
      onDone();
    },
  });

  const activePeople = (people.data?.people ?? []).filter((person) => person.status === 'active');
  return (
    <div data-territory-editor className="flex flex-col gap-3">
      {save.error ? (
        <p data-territory-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
          {errorText(save.error)}
        </p>
      ) : null}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-dense text-ink-600">
          {t('territory.name')}
          <input data-territory-name className={INPUT} value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-dense text-ink-600">
          {t('territory.priority')}
          <input data-territory-priority type="number" min={1} max={10000} className={INPUT} value={priority} onChange={(event) => setPriority(event.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-dense text-ink-600">
          {t('territory.owner')}
          <select data-territory-owner className={INPUT} value={ownerId} onChange={(event) => setOwnerId(event.target.value)}>
            <option value="">{t('territory.nobody')}</option>
            {activePeople.map((person) => (
              <option key={person.userId} value={person.userId}>
                {person.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-dense text-ink-600">
          {t('territory.team')}
          <select data-territory-team className={INPUT} value={teamId} onChange={(event) => setTeamId(event.target.value)}>
            <option value="">{t('territory.noTeam')}</option>
            {(people.data?.teams ?? []).map((team) => (
              <option key={team.teamId} value={team.teamId}>
                {team.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="flex items-center gap-2 text-dense text-ink">
        <input type="checkbox" data-territory-active checked={active} onChange={(event) => setActive(event.target.checked)} />
        {t('territory.activeLabel')}
      </label>

      <SectionLabel>{t('territory.criteria')}</SectionLabel>
      <RuleBuilder schema={schema} value={criteria} onChange={setCriteria} labels={ruleBuilderLabels(t)} matchCount={territory?.matching ?? null} />

      <SectionLabel>{t('territory.named')}</SectionLabel>
      <p className="text-meta text-ink-meta">{t('territory.namedHelp')}</p>
      {accounts.length > 0 ? (
        <ul className="flex flex-col">
          {accounts.map((account) => (
            <li key={account.accountId} data-territory-named={account.accountId} className="flex items-center gap-2 text-dense text-ink">
              <span className="min-w-0 flex-1 truncate">{account.name}</span>
              <Button intent="ghost" size="small" onClick={() => setAccounts((list) => list.filter((entry) => entry.accountId !== account.accountId))}>
                {t('territory.remove')}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      <label className="flex flex-col gap-1 text-dense text-ink-600">
        {t('territory.findCompany')}
        <input data-territory-search type="search" className={INPUT} value={search} onChange={(event) => setSearch(event.target.value)} />
      </label>
      {(found.data?.rows ?? []).length > 0 ? (
        <ul className="flex flex-col">
          {(found.data?.rows ?? [])
            .filter((row) => !accounts.some((account) => account.accountId === row.accountId))
            .map((row) => (
              <li key={row.accountId}>
                <button
                  type="button"
                  data-territory-add={row.accountId}
                  className="text-dense text-bronze-text hover:text-ink"
                  onClick={() => setAccounts((list) => [...list, { accountId: row.accountId, name: row.name }])}
                >
                  {t('territory.addCompany', { values: { name: row.name } })}
                </button>
              </li>
            ))}
        </ul>
      ) : null}

      <div className="flex gap-2">
        <Button intent="primary" size="small" data-territory-save disabled={save.isPending} onClick={() => save.mutate()}>
          {t('territory.save')}
        </Button>
        <Button intent="ghost" size="small" data-territory-cancel onClick={onDone}>
          {t('territory.cancel')}
        </Button>
      </div>
    </div>
  );
}
